/**
 * Turns however the app was launched into an AceStream link.
 *
 * Three entry points reach us:
 *   /watch?link=…   protocol handler (web+acestream://…) and manual deep links
 *   /share?text=…   Android share sheet, via the manifest's share_target
 *   /?link=…        plain query string, useful for bookmarks and QR codes
 */

const PATTERNS = [
  /acestream:\/\/[0-9a-f]{40}/i,
  /magnet:\?[^\s"'<>]+/i,
  /https?:\/\/\S+?(?:\.acelive|\.torrent|\/ace\/getstream\?\S+)/i,
  /\b[0-9a-f]{40}\b/i,
]

/**
 * The share sheet hands us whatever text surrounded the link, and the protocol
 * handler hands us the URL still carrying its `web+` registration prefix.
 */
export function extractAceLink(...candidates: Array<string | null | undefined>): string | null {
  for (const raw of candidates) {
    if (!raw) continue
    const text = decodeSafely(raw).replace(/\bweb\+acestream:/gi, 'acestream:')
    for (const pattern of PATTERNS) {
      const hit = pattern.exec(text)
      if (hit) return hit[0]
    }
  }
  return null
}

function decodeSafely(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    // Malformed percent-encoding: the raw text is still worth scanning.
    return value
  }
}

/** Reads the launch URL, or null if the app was opened normally. */
export function readLaunchLink(): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  const q = url.searchParams

  if (url.pathname === '/share') {
    return extractAceLink(q.get('text'), q.get('url'), q.get('title'))
  }
  return extractAceLink(q.get('link'))
}

/** Drops the launch parameters so a reload does not replay the same link. */
export function clearLaunchUrl(): void {
  if (typeof window === 'undefined') return
  const { pathname, search } = window.location
  if (pathname === '/' && !search) return
  window.history.replaceState(null, '', '/')
}
